import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Download, 
  Sparkles,
  Loader2,
  Globe,
  File
} from 'lucide-react';
import { generateCV, getApplicantProfile } from '../services/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Documents = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingCV, setGeneratingCV] = useState(false);
  const [cvLanguage, setCvLanguage] = useState('de');
  const [generatedCV, setGeneratedCV] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await getApplicantProfile();
      setProfile(res.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCV = async () => {
    setGeneratingCV(true);
    try {
      const res = await generateCV(cvLanguage);
      setGeneratedCV(res.data.cv_text);
      toast.success('Lebenslauf generiert');
    } catch (error) {
      toast.error('Fehler beim Generieren');
    } finally {
      setGeneratingCV(false);
    }
  };

  const handleDownloadPDF = () => {
    const token = localStorage.getItem('token');
    window.open(`${BACKEND_URL}/api/documents/cv-pdf`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Dokumente</h1>
          <p className="text-muted-foreground mt-1">
            Erstellen und verwalten Sie Ihre Bewerbungsunterlagen
          </p>
        </div>

        {/* Profile Warning */}
        {profile && profile.profile_complete < 50 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <p className="text-sm text-yellow-800">
                Vervollständigen Sie Ihr Profil zu mindestens 50%, um bessere Ergebnisse zu erhalten.
                Aktuell: {profile.profile_complete}%
              </p>
            </CardContent>
          </Card>
        )}

        {/* CV Generator */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Lebenslauf Generator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Lassen Sie Ihren Lebenslauf automatisch von der KI erstellen, basierend auf Ihrem Profil.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <Select value={cvLanguage} onValueChange={setCvLanguage}>
                  <SelectTrigger className="w-40" data-testid="cv-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">Englisch</SelectItem>
                    <SelectItem value="tr">Türkisch</SelectItem>
                    <SelectItem value="uk">Ukrainisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleGenerateCV} 
                disabled={generatingCV}
                className="rounded-full"
                data-testid="generate-cv-btn"
              >
                {generatingCV ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Lebenslauf generieren
              </Button>

              <Button 
                variant="outline"
                onClick={handleDownloadPDF}
                className="rounded-full"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Als PDF herunterladen
              </Button>
            </div>

            {generatedCV && (
              <div className="mt-6 p-6 bg-muted rounded-lg">
                <h4 className="font-semibold mb-4">Generierter Lebenslauf:</h4>
                <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans">
                  {generatedCV}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Tipps für Ihre Bewerbung</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">1.</span>
                Halten Sie Ihr Profil aktuell für bessere KI-Ergebnisse
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">2.</span>
                Passen Sie generierte Texte an Ihre persönliche Situation an
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">3.</span>
                Nutzen Sie mehrsprachige Lebensläufe für internationale Bewerbungen
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">4.</span>
                Generieren Sie für jeden Job ein individuelles Anschreiben
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Documents;
